import {ASSET_FIELD_KEYS} from '@sollapay/domain/asset-types';
import {TransactionDirection, TransactionGroupBy, TransactionStatus} from '@sollapay/enums';
import {dateToIso, getStartOfWeek, Money, parseMoney} from '@sollapay/utils';

import type {TransactionsFilterState} from '../config/transactions-filter.config';
import type {TrustAccountTransactionDTO, TrustAccountTransactionGroupDTO} from '@sollapay/types';

export function countTransactionsActiveFilters(filter: TransactionsFilterState): number {
  let count = 0;
  if (filter.datePreset !== 'all') count++;
  if (filter.amountMin != null || filter.amountMax != null) count++;
  if (filter.initiationMethods.length > 0) count++;
  if (filter.directions.length > 0) count++;
  if (filter.statuses.length > 0) count++;
  return count;
}

export function isTransactionsFilterActive(filter: TransactionsFilterState): boolean {
  return countTransactionsActiveFilters(filter) > 0;
}

export interface SearchableTransaction {
  tx: TrustAccountTransactionDTO;
  /** Kept per-field, not joined — a match must stay within one field. */
  searchFields: string[];
}

/** Recompute only when `transactions` itself changes, not on every search keystroke. */
export function buildSearchableTransactions(
  transactions: TrustAccountTransactionDTO[]
): SearchableTransaction[] {
  return transactions.map(tx => ({tx, searchFields: buildSearchFields(tx)}));
}

function buildSearchFields(tx: TrustAccountTransactionDTO): string[] {
  const fields =
    tx.direction === TransactionDirection.CREDIT
      ? [tx.buyerName, tx.asset?.displayCode, ...ASSET_FIELD_KEYS.map(key => tx.asset?.[key])]
      : [tx.creditorName, tx.description];
  return fields.filter((field): field is string => !!field).map(field => field.toLowerCase());
}

/**
 * Filters precomputed searchable transactions by filter state and search string.
 */
export function filterTransactions(
  searchableTransactions: SearchableTransaction[],
  filter: TransactionsFilterState,
  search: string
): TrustAccountTransactionDTO[] {
  const searchLower = search.toLowerCase().trim();
  return searchableTransactions
    .filter(({tx, searchFields}) => matchesFilter(tx, searchFields, filter, searchLower))
    .map(({tx}) => tx);
}

/**
 * Groups a flat list of transactions by the given strategy, sorts each group,
 * and computes totalBalanceNis from APPLIED transactions only.
 */
export function groupTransactions(
  transactions: TrustAccountTransactionDTO[],
  groupBy: TransactionGroupBy
): TrustAccountTransactionGroupDTO[] {
  const groupMap = new Map<string, TrustAccountTransactionDTO[]>();

  for (const tx of transactions) {
    const groupKey = resolveGroupKey(tx, groupBy);
    const existing = groupMap.get(groupKey);
    if (existing) {
      existing.push(tx);
    } else {
      groupMap.set(groupKey, [tx]);
    }
  }

  const entries = Array.from(groupMap.entries());

  entries.sort(([aKey], [bKey]) => {
    switch (groupBy) {
      case TransactionGroupBy.DATE:
        return bKey.localeCompare(aKey); // newest first
      case TransactionGroupBy.STATUS:
        if (aKey === TransactionStatus.APPLIED && bKey !== TransactionStatus.APPLIED) return -1;
        if (bKey === TransactionStatus.APPLIED && aKey !== TransactionStatus.APPLIED) return 1;
        return 0;
      case TransactionGroupBy.DIRECTION:
        if (aKey === TransactionDirection.CREDIT && bKey !== TransactionDirection.CREDIT) return -1;
        if (bKey === TransactionDirection.CREDIT && aKey !== TransactionDirection.CREDIT) return 1;
        return 0;
      default:
        return aKey.localeCompare(bKey);
    }
  });

  return entries.map(([groupKey, txs]) => ({
    groupKey,
    totalBalanceNis: computeGroupBalance(txs),
    transactionCount: txs.length,
    transactions: txs
  }));
}

function resolveGroupKey(tx: TrustAccountTransactionDTO, groupBy: TransactionGroupBy): string {
  switch (groupBy) {
    case TransactionGroupBy.DATE:
      return dateToIso(new Date(tx.effectiveDate));
    case TransactionGroupBy.COUNTERPARTY:
      return tx.direction === TransactionDirection.CREDIT
        ? (tx.buyerId ?? 'unknown')
        : (tx.creditorName ?? 'unknown');
    case TransactionGroupBy.PURCHASE:
      return tx.purchaseId ?? 'unknown';
    case TransactionGroupBy.STATUS:
      return tx.status;
    case TransactionGroupBy.INITIATION_METHOD:
      return tx.direction === TransactionDirection.CREDIT
        ? (tx.initiationMethod ?? 'unknown')
        : 'unknown';
    case TransactionGroupBy.DIRECTION:
      return tx.direction;
  }
}

function computeGroupBalance(txs: TrustAccountTransactionDTO[]): string {
  return txs
    .filter(tx => tx.status === TransactionStatus.APPLIED)
    .reduce((sum, tx) => {
      const amount = parseMoney(tx.amount);
      return tx.direction === TransactionDirection.CREDIT ? sum.add(amount) : sum.subtract(amount);
    }, Money.zero())
    .toString();
}

function resolveDatePresetCutoff(datePreset: TransactionsFilterState['datePreset']): Date | null {
  if (datePreset === 'all') return null;
  if (datePreset === 'this_week') return getStartOfWeek(new Date());
  const days = datePreset === '7d' ? 7 : datePreset === '30d' ? 30 : 90;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  return cutoff;
}

function matchesFilter(
  tx: TrustAccountTransactionDTO,
  searchFields: string[],
  filter: TransactionsFilterState,
  search: string
): boolean {
  if (search.length > 0 && !searchFields.some(field => field.includes(search))) return false;
  const dateCutoff = resolveDatePresetCutoff(filter.datePreset);
  if (dateCutoff && new Date(tx.effectiveDate) < dateCutoff) return false;
  if (filter.initiationMethods.length > 0) {
    if (tx.direction !== TransactionDirection.CREDIT) return false;
    if (tx.initiationMethod === null || !filter.initiationMethods.includes(tx.initiationMethod))
      return false;
  }
  if (filter.directions.length > 0 && !filter.directions.includes(tx.direction)) return false;
  if (filter.statuses.length > 0 && !filter.statuses.includes(tx.status)) return false;
  if (filter.amountMin != null || filter.amountMax != null) {
    const amount = parseMoney(tx.amount).toNumber();
    if (filter.amountMin != null && amount < filter.amountMin) return false;
    if (filter.amountMax != null && amount > filter.amountMax) return false;
  }
  return true;
}
